import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getQuestionById,
  searchQuestions,
  getHistoryStats,
  type SearchQuestionsParams,
  type HistoryStats
} from '../services/api'
import type { Question } from '@shared/types'

const DIFFICULTY_LABELS = {
  easy: '简单',
  medium: '中等',
  hard: '困难'
}

const DIFFICULTY_COLORS = {
  easy: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  hard: 'bg-red-100 text-red-800'
}

export default function HistoryPage() {
  const navigate = useNavigate()

  const [searchId, setSearchId] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchDifficulty, setSearchDifficulty] = useState('')
  const [searchKnowledgePoint, setSearchKnowledgePoint] = useState('')
  const [searchLiterature, setSearchLiterature] = useState('')

  const [searchResults, setSearchResults] = useState<Question[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)

  const [stats, setStats] = useState<HistoryStats | null>(null)
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set())

  const pageSize = 10

  // 加载统计信息
  useEffect(() => {
    loadStats()
    // 默认加载所有题目
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadStats = async () => {
    try {
      const data = await getHistoryStats()
      setStats(data)
    } catch (error) {
      console.error('加载统计信息失败:', error)
    }
  }

  const handleSearchById = async () => {
    if (!searchId.trim()) {
      setSearchError('请输入题目ID')
      return
    }

    setIsSearching(true)
    setSearchError(null)

    try {
      const question = await getQuestionById(searchId.trim())
      if (question) {
        setSearchResults([question])
        setTotalResults(1)
        setCurrentPage(0)
      } else {
        setSearchError(`未找到题目: ${searchId}`)
        setSearchResults([])
        setTotalResults(0)
      }
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : '搜索失败')
      setSearchResults([])
      setTotalResults(0)
    } finally {
      setIsSearching(false)
    }
  }

  const handleSearch = async (page = 0) => {
    setIsSearching(true)
    setSearchError(null)

    try {
      const params: SearchQuestionsParams = {
        keyword: searchKeyword || undefined,
        difficulty: searchDifficulty || undefined,
        knowledgePoint: searchKnowledgePoint || undefined,
        literatureName: searchLiterature || undefined,
        limit: pageSize,
        offset: page * pageSize
      }

      const result = await searchQuestions(params)
      setSearchResults(result.questions)
      setTotalResults(result.total)
      setCurrentPage(page)
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : '搜索失败')
      setSearchResults([])
      setTotalResults(0)
    } finally {
      setIsSearching(false)
    }
  }

  const handleClearFilters = () => {
    setSearchId('')
    setSearchKeyword('')
    setSearchDifficulty('')
    setSearchKnowledgePoint('')
    setSearchLiterature('')
    setSearchError(null)
    handleSearch(0)
  }

  const toggleQuestionExpand = (id: string) => {
    const newExpanded = new Set(expandedQuestions)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedQuestions(newExpanded)
  }

  const totalPages = Math.ceil(totalResults / pageSize)

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">题目历史记录</h1>
          <p className="text-gray-600 mt-2">
            搜索和检索已生成的题目
          </p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="btn btn-secondary"
        >
          返回首页
        </button>
      </div>

      {/* 统计信息 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card bg-blue-50 border-blue-200">
            <h3 className="text-sm font-medium text-gray-700">题目总数</h3>
            <p className="text-3xl font-bold text-blue-600 mt-2">{stats.total}</p>
          </div>
          <div className="card bg-green-50 border-green-200">
            <h3 className="text-sm font-medium text-gray-700">简单</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">{stats.byDifficulty.easy}</p>
          </div>
          <div className="card bg-yellow-50 border-yellow-200">
            <h3 className="text-sm font-medium text-gray-700">中等</h3>
            <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.byDifficulty.medium}</p>
          </div>
          <div className="card bg-red-50 border-red-200">
            <h3 className="text-sm font-medium text-gray-700">困难</h3>
            <p className="text-3xl font-bold text-red-600 mt-2">{stats.byDifficulty.hard}</p>
          </div>
        </div>
      )}

      {/* ID 搜索 */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">通过ID检索题目</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchById()}
            placeholder="输入题目ID（如：T-20250123-0001）"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            onClick={handleSearchById}
            disabled={isSearching}
            className="btn btn-primary"
          >
            {isSearching ? '搜索中...' : '检索'}
          </button>
        </div>
      </div>

      {/* 高级搜索 */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">高级搜索</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input
            type="text"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="关键词"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <select
            value={searchDifficulty}
            onChange={(e) => setSearchDifficulty(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">所有难度</option>
            <option value="easy">简单</option>
            <option value="medium">中等</option>
            <option value="hard">困难</option>
          </select>
          <input
            type="text"
            value={searchKnowledgePoint}
            onChange={(e) => setSearchKnowledgePoint(e.target.value)}
            placeholder="知识点"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="text"
            value={searchLiterature}
            onChange={(e) => setSearchLiterature(e.target.value)}
            placeholder="文献名称"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => handleSearch(0)}
            disabled={isSearching}
            className="btn btn-primary"
          >
            {isSearching ? '搜索中...' : '搜索'}
          </button>
          <button
            onClick={handleClearFilters}
            className="btn btn-secondary"
          >
            清除筛选
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {searchError && (
        <div className="card bg-red-50 border-red-200">
          <p className="text-sm text-red-800">{searchError}</p>
        </div>
      )}

      {/* 搜索结果 */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            搜索结果 ({totalResults} 个题目)
          </h2>
        </div>

        {searchResults.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {isSearching ? '搜索中...' : '暂无结果'}
          </div>
        ) : (
          <div className="space-y-4">
            {searchResults.map((question) => {
              const isExpanded = expandedQuestions.has(question.id || '')
              return (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  {/* 题目头部 */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          DIFFICULTY_COLORS[question.difficulty]
                        }`}>
                          {DIFFICULTY_LABELS[question.difficulty]}
                        </span>
                        {question.id && (
                          <span className="text-xs text-gray-500 font-mono">
                            {question.id}
                          </span>
                        )}
                      </div>
                      {question.literatureName && (
                        <p className="text-sm text-gray-600 mt-1">
                          📚 {question.literatureName}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => toggleQuestionExpand(question.id || '')}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      {isExpanded ? '收起' : '展开详情'}
                    </button>
                  </div>

                  {/* 题干预览 */}
                  <p className="text-gray-900 line-clamp-2 mb-2">
                    {question.question}
                  </p>

                  {/* 知识点 */}
                  {question.knowledgePoints && question.knowledgePoints.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {question.knowledgePoints.map((kp, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          {kp}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 展开的详细信息 */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      {/* 文献简介 */}
                      {question.literatureSummary && (
                        <div className="p-3 bg-amber-50 rounded-lg">
                          <h4 className="text-sm font-medium text-amber-900 mb-1">文献摘要</h4>
                          <p className="text-sm text-gray-700">{question.literatureSummary}</p>
                        </div>
                      )}

                      {/* 选项 */}
                      <div className="space-y-2">
                        {question.options.map((option, idx) => (
                          <div
                            key={idx}
                            className={`p-2 rounded ${
                              option.startsWith(question.answer + '.')
                                ? 'bg-green-50 border border-green-300'
                                : 'bg-gray-50'
                            }`}
                          >
                            <p className="text-sm text-gray-900">{option}</p>
                          </div>
                        ))}
                      </div>

                      {/* 答案 */}
                      <div className="p-2 bg-green-50 rounded border border-green-200">
                        <p className="text-sm font-medium text-green-900">
                          正确答案: {question.answer}
                        </p>
                      </div>

                      {/* 解析 */}
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-1">解析</h4>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{question.analysis}</p>
                      </div>

                      {/* 文献链接 */}
                      {question.literatureUrl && (
                        <div>
                          <a
                            href={question.literatureUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-700 underline"
                          >
                            📄 查看原文献
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => handleSearch(currentPage - 1)}
              disabled={currentPage === 0 || isSearching}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <span className="text-sm text-gray-600">
              第 {currentPage + 1} / {totalPages} 页
            </span>
            <button
              onClick={() => handleSearch(currentPage + 1)}
              disabled={currentPage >= totalPages - 1 || isSearching}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
